"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import {
  destinationsToWhereToGoItems,
  listPublishedDestinations,
  normalizeWhereToGoNext,
} from "@/platforms/customer/services/destinationService";
import DivertSupplierModal from "@/platforms/supplier/components/modals/DivertSupplierModal";

const normalizeItem = (item, index) => {
  const number = item.number || String(index + 1).padStart(2, "0");
  const keywords = item.keywords ?? (item.keyword ? [item.keyword] : []);
  return {
    id: item.id || `${number}-${item.title || "item"}`,
    number,
    title: item.title || "",
    keyword: item.keyword || keywords[0] || "",
    keywords,
    href: item.href,
    ...item,
  };
};

/**
 * Where to Go Next / Explore more — CMS only (TA-064).
 * - Pass `items` from country/city `whereToGoNext` when available.
 * - With no items prop, loads published destination cities (homepage/blog).
 * - Empty CMS → render nothing — never invent sample destinations (TA-024 / TA-064).
 */
const WhereToGoNextDesktop = ({
  heading = "Where to Go Next",
  items,
  onSelect,
  /** both (default) | mobile | desktop — split layout when parent already scopes breakpoint */
  variant = "both",
}) => {
  const router = useRouter();
  const itemsProvided = items !== undefined;
  const [cmsItems, setCmsItems] = useState([]);
  const [loading, setLoading] = useState(!itemsProvided);

  useEffect(() => {
    if (itemsProvided) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const countries = await listPublishedDestinations();
        if (cancelled) return;
        setCmsItems(destinationsToWhereToGoItems(countries));
      } catch {
        if (!cancelled) setCmsItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [itemsProvided]);

  const exploreItems = useMemo(() => {
    const source = itemsProvided
      ? normalizeWhereToGoNext(items)
      : cmsItems;
    if (!Array.isArray(source) || source.length === 0) return [];
    return source.map((x, i) => normalizeItem(x, i));
  }, [itemsProvided, items, cmsItems]);

  const handleSelect = (item) => {
    if (typeof onSelect === "function") {
      onSelect(item);
      return;
    }
    if (item?.href) router.push(item.href);
  };

  if (loading || exploreItems.length === 0) return null;

  const mobileSection = (
    <div
      className={
        variant === "desktop"
          ? ""
          : variant === "mobile"
            ? "ta-desktop-home-section ta-desktop-home-container my-5"
            : "lg:hidden ta-desktop-home-section ta-desktop-home-container my-5"
      }
    >
      <div className="font-normal text-gray-600 ta-desktop-home-heading mb-4">
        Explore more with Trips Adora
      </div>
      <div className="flex flex-wrap gap-2">
        {exploreItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => handleSelect(item)}
            className="relative inline-flex w-fit items-center rounded-md bg-primary py-2 pl-12 pr-4 text-left cities-text font-medium text-white whitespace-nowrap transition-colors hover:opacity-95 cursor-pointer"
            data-keyword={item.keyword}
          >
            <span className="absolute left-0 top-1/2 flex h-7.5 w-10 -translate-y-1/2 items-center justify-center rounded-s-md bg-secondary cities-text font-medium text-white span-text">
              {Number(item.number)}
            </span>
            <span className="leading-none span-text">{item.title}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const desktopSection = (
    <div
      className={
        variant === "mobile"
          ? ""
          : variant === "desktop"
            ? "ta-desktop-home-section ta-desktop-home-container"
            : "hidden lg:block ta-desktop-home-section ta-desktop-home-container"
      }
    >
      <div className="font-medium text-black ta-desktop-home-heading mb-4">
        {heading}
      </div>
      <div className="flex flex-wrap gap-3">
        {exploreItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => handleSelect(item)}
            className="relative inline-flex w-fit items-center rounded-md bg-primary py-2 pl-12 pr-4 text-left cities-text font-medium text-white whitespace-nowrap transition-colors hover:opacity-95 cursor-pointer"
            data-keyword={item.keyword}
          >
            <span className="absolute left-0 top-1/2 flex h-8 w-10 -translate-y-1/2 items-center justify-center rounded-s-md bg-secondary cities-text font-medium text-white span-text">
              {Number(item.number)}
            </span>
            <span className="leading-none span-text">{item.title}</span>
          </button>
        ))}
      </div>
    </div>
  );

  if (variant === "mobile") return mobileSection;
  if (variant === "desktop") return desktopSection;

  return (
    <>
      {mobileSection}
      {desktopSection}
    </>
  );
};

export default WhereToGoNextDesktop;
